/**
 * The customer account's own fields, shared by "Thêm khách hàng" and "Sửa thông tin khách hàng"
 * (a customer is an account plus its primary subject; identity data are the subject's, edited
 * with the subject form): Tài khoản (login e-mail, phone, notification language, plus the
 * caller's tier / currency / status) and Xuất hoá đơn ("giống chủ thể chính" or its
 * own buyer, tax code, address and e-mails; the tax code follows the primary subject's kind — a
 * person's CCCD, an enterprise's 10 or 10-3 digits, ../../lib/taxCode.ts). A duplicate e-mail
 * or tax code shows as a warning. Also the province / ward picker of the administrative list.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { api } from '../../api';
import type { CustomerDuplicate, NotificationLanguage } from '../../api/areas/accounts';
import type { VnWard } from '../../api/types';
import { splitEmails, type AccountDraft, type FieldProblem } from '../../lib/customerProfile';
import { useApi } from '../../lib/useApi';
import { cn } from '../../lib/utils';
import { paths } from './shared';

export const selectClass =
  'h-9 w-full min-w-0 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:opacity-50 dark:bg-input/30';

type Lookup = (key: string, o?: Record<string, unknown>) => string;

export function F({ label, error, hint, className, children }: { label: ReactNode; error?: string | null; hint?: ReactNode; className?: string; children: ReactNode }) {
  return (
    // eslint-disable-next-line jsx-a11y/label-has-associated-control -- the control is the child
    <label className={cn('grid content-start gap-1.5 text-sm', className)}>
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {children}
      {error ? <span className="text-xs text-critical">{error}</span> : hint ? <span className="text-xs text-muted-foreground">{hint}</span> : null}
    </label>
  );
}

export function Group({ title, children, className }: { title: ReactNode; children: ReactNode; className?: string }) {
  return (
    <fieldset className={cn('grid gap-3 rounded-lg border p-3 sm:grid-cols-2', className)}>
      <legend className="px-1 text-sm font-medium">{title}</legend>
      {children}
    </fieldset>
  );
}

/** Messages for the server's (and the form's own) field problems. */
export function useFieldErrors(problems: FieldProblem[]) {
  const { t } = useTranslation('accounts');
  const tr = t as unknown as Lookup;
  return (field: string) => {
    const e = problems.filter((x) => x.field === field);
    return e.length ? e.map((x) => tr(`customerForm.errors.${x.code}`, { defaultValue: x.code })).join(' · ') : null;
  };
}

/** Other accounts with the same e-mail / tax code, looked up while typing (a warning only). */
export function useDuplicates(email: string, taxCode: string, exclude?: string) {
  const e = email.trim().toLowerCase();
  const tax = taxCode.replace(/\s+/g, '');
  const query = { email: e.includes('@') ? e : undefined, tax_code: tax.length >= 10 ? tax : undefined, exclude };
  const key = JSON.stringify(query);
  const [found, setFound] = useState<{ key: string; items: CustomerDuplicate[] }>({ key: '', items: [] });
  useEffect(() => {
    const q = JSON.parse(key) as typeof query;
    if (!q.email && !q.tax_code) return;
    const ctl = new AbortController();
    const timer = setTimeout(() => {
      api
        .findCustomerDuplicates(q, { signal: ctl.signal })
        .then((r) => setFound({ key, items: r.items }))
        .catch(() => undefined);
    }, 400);
    return () => {
      clearTimeout(timer);
      ctl.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `key` is the query
  }, [key]);
  return found.key === key ? found.items : [];
}

export function DuplicateNote({ items }: { items: CustomerDuplicate[] }) {
  const { t } = useTranslation('accounts');
  if (!items.length) return null;
  return (
    <span className="text-xs text-warning">
      {t('customerForm.duplicate')}{' '}
      {items.map((c, i) => (
        <span key={c.customer_id}>
          {i ? ', ' : null}
          <Link className="underline" to={paths.customer(c.customer_id)} target="_blank" rel="noreferrer">
            {c.display_name}
          </Link>
        </span>
      ))}
    </span>
  );
}

/** Province, then a ward of it, from VNNIC's place list (the account's and the representative's). */
export function PlacePicker({
  provinceId,
  wardId,
  onChange,
  provinceError,
  wardError,
}: {
  provinceId: string;
  wardId: string;
  onChange: (patch: { provinceId?: string; wardId?: string }) => void;
  provinceError?: string | null;
  wardError?: string | null;
}) {
  const { t } = useTranslation('accounts');
  const provinces = useApi((signal) => api.listVnProvinces({ signal }), []);
  const pid = Number(provinceId) || 0;
  const wards = useApi((signal) => (pid ? api.listVnWards(pid, { signal }) : Promise.resolve({ items: [] as VnWard[] })), [pid]);
  const provinceItems = provinces.status === 'success' ? provinces.data.items : [];
  const wardItems = wards.status === 'success' ? wards.data.items : [];
  return (
    <>
      <F label={t('customerForm.province')} error={provinceError}>
        <select className={selectClass} value={provinceId} onChange={(e) => onChange({ provinceId: e.target.value, wardId: '' })}>
          <option value="">{provinces.status === 'loading' ? t('customerForm.loading') : t('customerForm.choose')}</option>
          {provinceItems.map((p) => (
            <option key={p.id} value={p.id}>
              {p.full_name}
            </option>
          ))}
        </select>
      </F>
      <F label={t('customerForm.ward')} error={wardError}>
        <select className={selectClass} value={wardId} disabled={!pid} onChange={(e) => onChange({ wardId: e.target.value })}>
          <option value="">{pid ? (wards.status === 'loading' ? t('customerForm.loading') : t('customerForm.choose')) : t('customerForm.provinceFirst')}</option>
          {wardItems.map((w) => (
            <option key={w.id} value={w.id}>
              {w.name}
            </option>
          ))}
        </select>
      </F>
    </>
  );
}

/** Tài khoản: the login e-mail, phone and notification language, and the caller's extra fields
 * (tier, currency, status). */
export function AccountFields({
  d,
  set,
  problems,
  country,
  excludeId,
  taxCode,
  children,
}: {
  d: AccountDraft;
  set: (patch: Partial<AccountDraft>) => void;
  problems: FieldProblem[];
  /** The primary subject's address country (how a national phone number is read). */
  country: string;
  /** The account being edited (not its own duplicate). */
  excludeId?: string;
  /** The primary subject's enterprise tax code (duplicate warning). */
  taxCode?: string;
  children?: ReactNode;
}) {
  const { t } = useTranslation('accounts');
  const err = useFieldErrors(problems);
  const vn = country.toUpperCase() === 'VN';
  const duplicates = useDuplicates(d.email, taxCode ?? '', excludeId);
  return (
    <Group title={t('customerForm.section.account')}>
      <F label={t('customerForm.email')} error={err('email')} hint={<DuplicateNote items={duplicates.filter((x) => x.field === 'email')} />}>
        <Input type="email" value={d.email} onChange={(e) => set({ email: e.target.value })} autoComplete="off" data-field="account-email" />
      </F>
      <F label={t('customerForm.phone')} error={err('phone')} hint={vn ? t('customerForm.phoneHintVn') : t('customerForm.phoneHint')}>
        <Input type="tel" value={d.phone} onChange={(e) => set({ phone: e.target.value })} placeholder={vn ? '0912 345 678' : '+65 6123 4567'} />
      </F>
      <F label={t('customerForm.language')} error={err('profile.notification_language')}>
        <select className={selectClass} value={d.language} onChange={(e) => set({ language: e.target.value as NotificationLanguage })}>
          <option value="vi">{t('customerForm.languages.vi')}</option>
          <option value="en">{t('customerForm.languages.en')}</option>
        </select>
      </F>
      {children}
      {duplicates.some((x) => x.field === 'tax_code') ? (
        <p className="sm:col-span-2">
          <DuplicateNote items={duplicates.filter((x) => x.field === 'tax_code')} />
        </p>
      ) : null}
    </Group>
  );
}

/** Xuất hoá đơn: "giống chủ thể chính" (read-only preview of what the invoice carries) or the
 * buyer's own details. */
export function InvoiceFields({
  d,
  set,
  problems,
  preview,
  personalTaxCode,
}: {
  d: AccountDraft;
  set: (patch: Partial<AccountDraft>) => void;
  problems: FieldProblem[];
  /** What "giống chủ thể chính" puts on the invoice. */
  preview: { buyer: string; taxCode: string | null; address: string | null; emails: string[] };
  /** The primary subject is a person or a household business: the tax code is a CCCD. */
  personalTaxCode: boolean;
}) {
  const { t } = useTranslation('accounts');
  const err = useFieldErrors(problems);
  return (
    <Group title={t('customerForm.section.invoice')}>
      <label className="flex items-center gap-2 text-sm sm:col-span-2">
        <Checkbox
          checked={d.invoiceSame}
          onCheckedChange={(v) =>
            set(
              v === true
                ? { invoiceSame: true }
                : {
                    invoiceSame: false,
                    invoiceBuyerName: d.invoiceBuyerName || preview.buyer,
                    invoiceAddress: d.invoiceAddress || (preview.address ?? ''),
                    invoiceEmails: d.invoiceEmails || preview.emails.join(', '),
                  },
            )
          }
        />
        {t('customerForm.invoiceSame')}
      </label>
      {d.invoiceSame ? (
        <dl className="grid gap-x-4 gap-y-1 text-sm sm:col-span-2 sm:grid-cols-[max-content_1fr]">
          <dt className="text-muted-foreground">{t('customerForm.invoiceBuyer')}</dt>
          <dd>{preview.buyer || '—'}</dd>
          <dt className="text-muted-foreground">{t('customerForm.invoiceTaxCode')}</dt>
          <dd>{preview.taxCode ?? '—'}</dd>
          <dt className="text-muted-foreground">{t('customerForm.invoiceAddress')}</dt>
          <dd>{preview.address ?? '—'}</dd>
          <dt className="text-muted-foreground">{t('customerForm.invoiceEmails')}</dt>
          <dd>{preview.emails.join(', ') || '—'}</dd>
        </dl>
      ) : (
        <>
          <F label={t('customerForm.invoiceBuyer')} error={err('profile.invoice.buyer_name')} className="sm:col-span-2">
            <Input value={d.invoiceBuyerName} onChange={(e) => set({ invoiceBuyerName: e.target.value })} maxLength={255} />
          </F>
          <F label={t('customerForm.invoiceTaxCode')} error={err('profile.invoice.tax_code')} hint={personalTaxCode ? t('customerForm.invoiceTaxCodePersonHint') : t('customerForm.invoiceTaxCodeOrgHint')}>
            <Input value={d.invoiceTaxCode} inputMode="numeric" onChange={(e) => set({ invoiceTaxCode: e.target.value })} />
          </F>
          <F label={t('customerForm.invoiceEmails')} error={err('profile.invoice.emails')} hint={t('customerForm.invoiceEmailsHint', { count: splitEmails(d.invoiceEmails).length })}>
            <Input value={d.invoiceEmails} onChange={(e) => set({ invoiceEmails: e.target.value })} />
          </F>
          <F label={t('customerForm.invoiceAddress')} className="sm:col-span-2">
            <Input value={d.invoiceAddress} onChange={(e) => set({ invoiceAddress: e.target.value })} maxLength={500} />
          </F>
        </>
      )}
    </Group>
  );
}
